import {CommonModule} from '@angular/common';
import {Component} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faCancel,
  faEye,
  faFileUpload,
  faPen,
  faPlus,
  faSquarePlus,
  faSquareXmark,
  faTrash,
  faUpload
} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';
import {ApiService} from '../../services/api.service';
import {AuthService} from '../../services/auth.service';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../../shared/constants/pagination.constants';
import {VtePaginationComponent} from '../../shared/vte/vte-pagination/vte-pagination.component';

type SubscriptionRow = {
  id: number;
  organization: number;
  organization_name: string;
  poc_name: string;
  poc_email: string;
  poc_phone: string;
  customer_poc_name?: string;
  customer_poc_email?: string;
  customer_poc_phone?: string;
  kyra_sales_poc_name?: string;
  kyra_sales_poc_email?: string;
  kyra_sales_poc_phone?: string;
  billing_type: string;
  start_date: string;
  next_payment_due_date?: string | null;
  expiry_date: string;
  renewal_date: string;
  contract_duration_years: number;
  total_amount: string;
  total_paid_amount?: string;
  pending_amount?: string;
  currency: string;
  cameras?: number | null;
  status: string;
};

@Component({
    selector: 'app-subscriptions',
    imports: [CommonModule, FormsModule, FontAwesomeModule, VtePaginationComponent],
    templateUrl: './subscriptions.component.html',
    styleUrls: ['./subscriptions.component.css']
})
export class SubscriptionsComponent {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
  subscriptions: SubscriptionRow[] = [];
  organizations: Array<{ id: string | number; name: string }> = [];
  selectedOrgId: string = '';
  searchText = '';
  statusFilter = '';
  billingFilter = '';

  recordsPerPage = 10;
  currentPage = 1;
  totalRecords = 0;
  totalPages = 1;
  nextPage: string | null = null;
  previousPage: string | null = null;
  loading = false;

  isSuperUser = false;
  isAdmin = false;

  quotationFile: File | null = null;
  uploadError = '';
  uploadSuccess = '';
  selectedFileName = 'No file chosen';
  quotationCustomerPocEmail = '';
  quotationCustomerPocPhone = '';
  quotationSubscriptionStartDate = '';

  faEye = faEye;
  faPen = faPen;
  faTrash = faTrash;
  faPlus = faPlus;
  faUpload = faUpload;
  showConfirmationDialog: boolean = false;
  showUploadModal: boolean = false;
  isDragOver: boolean = false;
  pId!: number;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private toastr: ToastrService,
    library: FaIconLibrary,
  ) {
    library.addIcons(faTrash,
                    faUpload,
                    faPen,
                    faEye,
                    faAngleLeft,
                    faAngleRight,
                    faSquarePlus,
                    faCancel,
                    faSquareXmark,
                    faFileUpload
                  );
  }

  ngOnInit() {
    const user = localStorage.getItem('user');
    if (user) {
      const userObj = JSON.parse(user);
        this.isSuperUser = this.authService.isSuperUser();
      this.isAdmin = userObj['groups'] === 'Admin';
    }
    if (this.isSuperUser) {
      this.fetchOrganizations();
    }
    this.fetchSubscriptions();
  }

  fetchOrganizations() {
    this.apiService.getOrganizations().subscribe((data: any) => {
      if (Array.isArray(data?.results)) {
        this.organizations = data.results;
        return;
      }
      if (data && typeof data === 'object') {
        this.organizations = Object.entries(data).map(([id, name]) => ({ id, name: String(name) }));
        return;
      }
      this.organizations = [];
    });
  }

  fetchSubscriptions() {
    this.loading = true;
    const orgId = this.isSuperUser ? this.selectedOrgId || null : null;
    this.apiService.listSubscriptions(
      this.currentPage,
      this.recordsPerPage,
      this.searchText,
      this.statusFilter,
      this.billingFilter,
      orgId
    ).subscribe({
      next: (data: any) => {
        this.subscriptions = data?.results ?? [];
        this.totalRecords = data?.count ?? 0;
        this.nextPage = data?.next ?? null;
        this.previousPage = data?.previous ?? null;
        this.totalPages = Math.max(1, Math.ceil(this.totalRecords / this.recordsPerPage));
        this.loading = false;
      },
      error: () => {
        this.subscriptions = [];
        this.totalRecords = 0;
        this.totalPages = 1;
        this.loading = false;
      }
    });
  }

  onFiltersChange() {
    this.currentPage = 1;
    this.fetchSubscriptions();
  }

  next() {
    if (this.nextPage) {
      this.currentPage += 1;
      this.fetchSubscriptions();
    }
  }

  previous() {
    if (this.previousPage) {
      this.currentPage -= 1;
      this.fetchSubscriptions();
    }
  }

  selectPageSize() {
    this.currentPage = 1;
    this.fetchSubscriptions();
  }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.fetchSubscriptions();
    }

    onPageSizeChange(pageSize: number): void {
        this.recordsPerPage = pageSize;
        this.currentPage = 1;
        this.fetchSubscriptions();
    }

  openCreate() {
    this.router.navigate(['subscriptions/new']);
  }

  openDetail(id: number) {
    this.router.navigate([`subscriptions/${id}`]);
  }

  openEdit(id: number) {
    this.router.navigate([`subscriptions/${id}/edit`]);
  }

  deleteSubscription() {
    this.apiService.deleteSubscription(this.pId).subscribe({
      next: () => {
        this.toastr.success('Subscription deleted successfully.', 'Success');
        this.closeConfirmationDialog();
        this.fetchSubscriptions();
      },
      error: (err) => {
        this.toastr.error(err?.error?.detail || 'Unable to delete subscription.', 'Error');
        this.closeConfirmationDialog();
      }
    });
  }

  openUploadModal() {
    this.uploadError = '';
    this.uploadSuccess = '';
    this.resetQuotationUploadForm();
    this.showUploadModal = true;
  }

  closeUploadModal() {
    this.showUploadModal = false;
    this.resetQuotationUploadForm();
  }

  clearSelectedFile() {
    this.quotationFile = null;
    this.selectedFileName = 'No file chosen';
    this.uploadError = '';
  }

  private resetQuotationUploadForm() {
    this.quotationFile = null;
    this.selectedFileName = 'No file chosen';
    this.uploadError = '';
    this.quotationCustomerPocEmail = '';
    this.quotationCustomerPocPhone = '';
    this.quotationSubscriptionStartDate = '';
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file && file.type === 'application/pdf') {
        if (!this.acceptSubscriptionUpload(file)) {
            return;
        }
      this.quotationFile = file;
      this.selectedFileName = file.name;
      this.uploadError = '';
    } else {
      this.uploadError = 'Please drop a PDF file.';
    }
  }

  openConfirmationDialog(index: number) {
    this.showConfirmationDialog = true;
    this.pId = index;
  }

  closeConfirmationDialog() {
    this.showConfirmationDialog = false;
  }

  onQuotationSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
        const file = input.files[0];
        if (!this.acceptSubscriptionUpload(file)) {
            input.value = '';
            this.quotationFile = null;
            this.selectedFileName = 'No file chosen';
            return;
        }
        this.quotationFile = file;
        this.selectedFileName = file.name;
      this.uploadError = '';
      this.uploadSuccess = '';
      return;
    }
    this.quotationFile = null;
    this.selectedFileName = 'No file chosen';
  }

    private acceptSubscriptionUpload(file: File): boolean {
        const maxSize = 20 * 1024 * 1024; // 20 MB — matches backend SUBSCRIPTION_DOCUMENT_MAX_BYTES
        if (file.size > maxSize) {
            this.uploadError = 'File size should be 20 MB or less.';
            this.toastr.error(this.uploadError, 'File Too Large');
            return false;
        }
        return true;
    }

  uploadQuotation() {
    if (!this.quotationFile) {
      this.uploadError = 'Please select a quotation PDF first.';
      return;
    }
    this.uploadError = '';
    this.uploadSuccess = '';
    const formData = new FormData();
    formData.append('file', this.quotationFile);
    if (this.quotationCustomerPocEmail.trim()) {
      formData.append('customer_poc_email', this.quotationCustomerPocEmail.trim());
    }
    if (this.quotationCustomerPocPhone.trim()) {
      formData.append('customer_poc_phone', this.quotationCustomerPocPhone.trim());
    }
    if (this.quotationSubscriptionStartDate.trim()) {
      formData.append('subscription_start_date', this.quotationSubscriptionStartDate.trim());
    }
    this.apiService.uploadSubscriptionQuotation(formData).subscribe({
      next: () => {
        this.resetQuotationUploadForm();
        this.uploadSuccess = 'Quotation processed successfully.';
        this.showUploadModal = false;
        this.toastr.success('Quotation processed successfully.', 'Success');
        this.fetchSubscriptions();
      },
      error: (err) => {
        const missingLabels = Array.isArray(err?.error?.missing_labels) ? err.error.missing_labels : [];
        const missingHelp = err?.error?.missing_help && typeof err.error.missing_help === 'object'
          ? Object.values(err.error.missing_help).join(' ')
          : '';
        if (missingLabels.length) {
          this.uploadError = `Unable to parse quotation. Missing: ${missingLabels.join(', ')}.${missingHelp ? ` ${missingHelp}` : ''}`;
          return;
        }
          // Interceptor already toasts HTTP 413; avoid a second generic toast.
          if (err?.status === 413) {
              this.uploadError = 'File is too large to upload. Please choose a smaller file.';
              return;
        }
        this.uploadError = err?.error?.detail || 'Unable to upload quotation.';
        this.toastr.error(this.uploadError, 'Error');
      }
    });
  }

  getCustomerPocName(subscription: SubscriptionRow): string {
    return subscription.customer_poc_name || subscription.poc_name || '-';
  }

  getCustomerPocEmail(subscription: SubscriptionRow): string {
    return subscription.customer_poc_email || subscription.poc_email || '-';
  }
}
